import createAsyncMutex from '@gaia/utils/createAsyncMutex';
import debounceWithResolver from '@gaia/utils/debounceWithResolver';
import fetchJSON from '@gaia/utils/fetchJSON';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import fetchElevation from './fetchElevation';

const debouncedFetch = debounceWithResolver(window.fetch, 500);
export const savedPoints: Record<string, Waypoint | TrackPoint> = {};

export function getIsWaypointModifiedSinceSave(point: Waypoint | TrackPoint) {
    const savedPoint = savedPoints[point.properties.localId];
    const omitProps = [
        'properties.id',
        'properties.localId',
        'properties.isLocallyCreated',
        'properties.isLatestImport',
    ];

    if (!savedPoint && point.properties.deleted) return false;
    else if (!savedPoint) return true;
    else if (isEqual(omit(savedPoint, omitProps), omit(point, omitProps))) return false;
    else if (savedPoint.properties.deleted) return false;
    else return true;
}

export function registerSavedWaypoint(point: Waypoint | TrackPoint) {
    savedPoints[point.properties.localId] = point;
}

export default createAsyncMutex(
    async (point: TrackPoint | Waypoint, setId: (id: string) => void): Promise<void> => {
        const isModifiedSinceSave = getIsWaypointModifiedSinceSave(point);
        if (!isModifiedSinceSave) return;
        registerSavedWaypoint(point);

        const body = JSON.stringify({
            type: 'Feature',
            geometry: await fetchElevation(point.geometry),
            properties: {
                ...omit(point.properties, 'latitude', 'longitude'),
                title: point.properties.title || 'Untitled Waypoint',
            },
        });

        if (point.properties.id) {
            await debouncedFetch(`/api/objects/waypoint/${point.properties.id}/`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
        } else {
            const response = await fetchJSON('/api/objects/waypoint/', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
            setId(response.properties.id);
        }
    },
    (point) => point.properties.localId
);
